import React, { useEffect, useMemo, useState } from 'react';
import Vimeo from '@u-wave/react-vimeo';
import { PageVideo } from '@scormhero/shared';
import { useContextSelector } from 'use-context-selector';

import styles from './VideoVimeo.module.scss';
import { UIContext } from '../../context/UI/UIContext';
import { handleSetLastTimeAt } from '../VideoHtml/VideoHtml';
import { toErrorWithMessage } from '../../utils/errorWithMessage';
import EmptyState from '../../UI/EmptyState/EmptyState';

type VideoVimeoProps = PageVideo & {
  url: string;
  globalState: any;
};

function VideoVimeo({
  resourceId,
  url,
  settings = {},
  globalState,
}: VideoVimeoProps) {
  const {
    onPageCompleted,
    getCurrentPage,
    getTracking,
    patchTracking,
    getColorScheme,
  } = globalState;
  const { disableSeek } = settings;
  const currentPage = getCurrentPage();
  const pageTracking = getTracking(currentPage.resourceId);
  const pageCompleted = pageTracking?.completed;
  const startBlinkNext = useContextSelector(UIContext, (c) => c.startBlinkNext);
  const [lastTimeAt, setLastTimeAt] = useState<{
    resourceId: string; // using resourceId here to make sure is lastTime from THAT resourceId
    time: number;
  }>({ resourceId: '', time: 0 });
  const [startAtTime, setStartAtTime] = useState<number>();
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const { mediaBackground } = getColorScheme();
  const [error, setError] = useState<string>();

  useEffect(() => {
    function onMount() {
      setStartAtTime(getTracking(resourceId)?.time);
      setIsMounted(true);
    }

    function onUmmount() {
      // reset in case next page is also a video
      setStartAtTime(0);
      setLastTimeAt({ resourceId: '', time: 0 });
      setIsMounted(false);
    }

    onMount();

    return () => onUmmount();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (
      isMounted &&
      lastTimeAt.time > 0 &&
      lastTimeAt.resourceId === resourceId // this check is required to avoid save time from previous video (if has in previous page)
    ) {
      patchTracking(resourceId, { time: lastTimeAt.time });
    }
  }, [lastTimeAt.time, lastTimeAt.resourceId, resourceId, isMounted]); // eslint-disable-line

  const vimeoVideoId = useMemo(() => {
    try {
      return url.split('/').pop();
    } catch (err) {
      return url; // could be just the id
    }
  }, [url]);

  // reset error when vimeoVideoId changes
  useEffect(() => setError(undefined), [vimeoVideoId]);

  if (!vimeoVideoId || !isMounted) return null;

  if (error)
    return (
      <div className={styles.errorContainer}>
        <EmptyState type="Error" description={error} />
      </div>
    );

  return (
    <Vimeo
      video={vimeoVideoId}
      autoplay
      className={styles.root}
      style={{ backgroundColor: mediaBackground }}
      width="100%"
      height="100%"
      responsive
      controls={disableSeek && !pageCompleted ? false : true}
      start={startAtTime}
      onEnd={() => {
        onPageCompleted(() => startBlinkNext());
        // reset tracking for the next visit return to the begining of the video
        patchTracking(resourceId, { time: 0 });
      }}
      onTimeUpdate={(e) => {
        setLastTimeAt((prevTime) => {
          if (!e.seconds) return prevTime;

          const _lastTime = handleSetLastTimeAt(prevTime.time, e.seconds);

          return { resourceId, time: _lastTime };
        });
      }}
      onError={(e) => setError(toErrorWithMessage(e).message)}
    />
  );
}

export default VideoVimeo;
